import { DestroyRef, inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SwUpdate } from '@angular/service-worker';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { filter, fromEvent, interval, merge, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { APP_UPDATE_CONFIG, AppUpdateConfig } from './app-update.tokens';
import { reportErrorExternally } from '../report-error-externally';
import {
  AppRecoveryComponent,
  AppRecoveryData,
} from './app-recovery.component';

const DEFAULT_CONFIG: AppUpdateConfig = {
  checkIntervalMs: 60_000,
  snackbarDurationMs: 15_000,
  messages: {
    updateAvailable: 'Neue Version verfuegbar.',
    reloadAction: 'Neu laden',
  },
};

const CHUNK_RECOVERY_STORAGE_KEY = 'blankmile:app-update:chunk-recovery-target';
const RECOVERY_TIMEOUT_MS = 10_000;

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly sw = inject(SwUpdate);
  private readonly snack = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router, { optional: true });
  private readonly configOverrides = inject(APP_UPDATE_CONFIG, {
    optional: true,
  });

  private readonly config: AppUpdateConfig = {
    ...DEFAULT_CONFIG,
    ...this.configOverrides,
    messages: {
      ...DEFAULT_CONFIG.messages,
      ...this.configOverrides?.messages,
    },
  };

  private checkInProgress = false;
  private initialCheckInProgress = true;
  private updatePending = false;
  private updatePromptShown = false;
  private assetRecoveryInProgress = false;
  private recoveryDialog: MatDialogRef<AppRecoveryComponent> | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => this.recoveryDialog?.close());
    this.registerNavigationRecovery();

    if (!this.sw.isEnabled) {
      return;
    }

    this.registerUnrecoverableRecovery();
    this.registerVersionUpdates();

    const worker = navigator.serviceWorker;
    if (worker?.controller) {
      this.startUpdateChecks();
    } else if (worker) {
      // Registration may wait for application stability for up to 30 seconds.
      fromEvent(worker, 'controllerchange')
        .pipe(
          filter(() => !!worker.controller),
          take(1),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => this.startUpdateChecks());
    }
  }

  private startUpdateChecks(): void {
    void this.runInitialCheck();

    merge(
      interval(this.config.checkIntervalMs),
      fromEvent(window, 'focus'),
      fromEvent(window, 'online'),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.runPeriodicCheck());
  }

  private registerNavigationRecovery(): void {
    if (!this.router) {
      return;
    }

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.clearChunkRecoveryTarget();
        this.recoveryDialog?.close();
      });

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationError => event instanceof NavigationError,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        if (!this.isDynamicImportFailure(event.error)) {
          return;
        }

        this.reportError(event.error);
        void this.reloadForAssetRecovery(this.navigationTarget(event.url));
      });
  }

  private registerUnrecoverableRecovery(): void {
    this.sw.unrecoverable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        this.reportError(new Error(event.reason));
        // The worker can report a missing chunk before Router emits its error.
        // Keep the in-flight destination instead of reloading the previous page.
        const target = this.router
          ?.currentNavigation()
          ?.extractedUrl.toString();
        void this.reloadForAssetRecovery(
          target ? this.navigationTarget(target) : location.href,
        );
      });
  }

  private navigationTarget(url: string): string {
    return new URL(url.replace(/^\//, ''), document.baseURI).href;
  }

  private async runInitialCheck(): Promise<void> {
    try {
      const hasUpdate = await this.checkForUpdateSafely();
      this.updatePending ||= hasUpdate;
      if (this.updatePending) {
        await this.activateAndReload();
      }
    } finally {
      this.initialCheckInProgress = false;
    }
  }

  private async runPeriodicCheck(): Promise<void> {
    if (this.updatePromptShown || this.initialCheckInProgress) {
      return;
    }

    if (!this.updatePending) {
      const hasUpdate = await this.checkForUpdateSafely();
      this.updatePending ||= hasUpdate;
    }
    this.openReloadPrompt();
  }

  private registerVersionUpdates(): void {
    this.sw.versionUpdates
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.type === 'VERSION_READY') {
          if (event.currentVersion.hash === event.latestVersion.hash) {
            return;
          }
          this.updatePending = true;
          if (!this.initialCheckInProgress) {
            this.openReloadPrompt();
          }
        } else if (event.type === 'VERSION_INSTALLATION_FAILED') {
          this.reportError(
            new Error(
              `Service worker version ${event.version.hash} failed to install: ${event.error}`,
            ),
          );
        }
      });
  }

  private async checkForUpdateSafely(): Promise<boolean> {
    if (this.checkInProgress || !navigator.serviceWorker?.controller) {
      return false;
    }

    this.checkInProgress = true;

    try {
      return await this.withTimeout(this.sw.checkForUpdate());
    } catch (error: unknown) {
      this.reportError(error);
      return false;
    } finally {
      this.checkInProgress = false;
    }
  }

  private openReloadPrompt(): void {
    if (
      !this.updatePending ||
      this.updatePromptShown ||
      this.assetRecoveryInProgress
    ) {
      return;
    }
    this.updatePromptShown = true;

    const prompt = this.snack.open(
      this.config.messages.updateAvailable,
      this.config.messages.reloadAction,
      {
        duration: this.config.snackbarDurationMs,
      },
    );

    prompt
      .onAction()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.activateAndReload());

    prompt
      .afterDismissed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updatePromptShown = false;
      });
  }

  private async activateAndReload(target = location.href): Promise<boolean> {
    try {
      await this.withTimeout(this.sw.activateUpdate());
      this.updatePending = false;
      this.reloadPage(target);
      return true;
    } catch (error: unknown) {
      this.reportError(error);
      return false;
    }
  }

  private reportError(error: unknown): void {
    reportErrorExternally(error);
  }

  private isDynamicImportFailure(error: unknown): boolean {
    const message = this.getErrorMessage(error).toLowerCase();

    return (
      message.includes('failed to fetch dynamically imported module') ||
      message.includes('importing a module script failed') ||
      message.includes('chunkloaderror') ||
      message.includes('loading chunk')
    );
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return `${error.name}: ${error.message}`;
    }

    if (typeof error === 'string') {
      return error;
    }

    if (error && typeof error === 'object' && 'message' in error) {
      const message = (error as { message?: unknown }).message;
      if (typeof message === 'string') {
        return message;
      }
    }

    return '';
  }

  private async reloadForAssetRecovery(target = location.href): Promise<void> {
    if (this.assetRecoveryInProgress || this.recoveryDialog) {
      return;
    }

    const url = new URL(target);
    url.searchParams.delete('ngsw-bypass');
    const nextTarget = url.href;
    const currentTarget = this.getChunkRecoveryTarget();

    if (currentTarget === nextTarget) {
      this.showRecovery(nextTarget);
      return;
    }

    if (!this.setChunkRecoveryTarget(nextTarget)) {
      this.showRecovery(nextTarget);
      return;
    }
    this.assetRecoveryInProgress = true;

    try {
      if (this.sw.isEnabled) {
        const hasUpdate = await this.checkForUpdateSafely();
        if (hasUpdate) {
          if (!(await this.activateAndReload(nextTarget))) {
            this.showRecovery(nextTarget);
          }
          return;
        }
      }

      this.reloadPage(nextTarget);
    } finally {
      this.assetRecoveryInProgress = false;
    }
  }

  private showRecovery(target: string): void {
    if (this.recoveryDialog || this.destroyRef.destroyed) {
      return;
    }

    this.recoveryDialog = this.dialog.open<
      AppRecoveryComponent,
      AppRecoveryData
    >(AppRecoveryComponent, {
      data: { reload: () => this.reloadLatestVersion(target) },
      role: 'dialog',
      hasBackdrop: true,
      width: '32rem',
      maxWidth: 'calc(100vw - 2rem)',
      autoFocus: 'first-heading',
      ariaLabelledBy: 'app-recovery-title',
      ariaDescribedBy: 'app-recovery-description',
      ariaModal: true,
    });
    this.recoveryDialog
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.recoveryDialog = null;
      });
  }

  private async reloadLatestVersion(target: string): Promise<void> {
    if (!navigator.onLine) {
      throw new Error('Cannot reload the latest version while offline');
    }

    // Localized apps share an origin. Reset only this app's registration/caches.
    const scope = new URL('./', document.baseURI);
    if ('serviceWorker' in navigator) {
      const registration = await this.withTimeout(
        navigator.serviceWorker.getRegistration(scope.href),
      );
      if (registration?.scope === scope.href) {
        await this.withTimeout(registration.unregister());
      }
    }
    if ('caches' in window) {
      const prefix = `ngsw:${scope.pathname}:`;
      const keys = await this.withTimeout(window.caches.keys());
      await this.withTimeout(
        Promise.all(
          keys
            .filter((key) => key.startsWith(prefix))
            .map((key) => window.caches.delete(key)),
        ),
      );
    }

    // Retain the retry guard until navigation succeeds, including after reset.
    this.setChunkRecoveryTarget(target);
    const url = new URL(target);
    url.searchParams.set('ngsw-bypass', 'true');
    this.reloadPage(url.href);
  }

  private async withTimeout<T>(operation: Promise<T>): Promise<T> {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        operation,
        new Promise<never>((_, reject) => {
          timeout = setTimeout(
            () => reject(new Error('App recovery timed out')),
            RECOVERY_TIMEOUT_MS,
          );
        }),
      ]);
    } finally {
      clearTimeout(timeout);
    }
  }

  private getChunkRecoveryTarget(): string | null {
    try {
      return sessionStorage.getItem(CHUNK_RECOVERY_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private setChunkRecoveryTarget(target: string): boolean {
    try {
      sessionStorage.setItem(CHUNK_RECOVERY_STORAGE_KEY, target);
      return true;
    } catch {
      // Without a persistent retry guard an automatic reload could loop.
      return false;
    }
  }

  private clearChunkRecoveryTarget(): void {
    try {
      sessionStorage.removeItem(CHUNK_RECOVERY_STORAGE_KEY);
    } catch {
      // Ignore storage failures.
    }
  }

  private reloadPage(target = location.href): void {
    if (target === location.href) {
      location.reload();
    } else {
      location.replace(target);
    }
  }
}
